import { Video, Clock, Bell, Target } from 'lucide-react';

/**
 * Mapping enums backend -> présentation FR.
 * Le backend expose : type = meeting|appointment|reminder|task,
 * status = scheduled|completed|cancelled, priority = low|medium|high.
 * La couleur n'est PAS stockée côté backend : elle est dérivée du type ici.
 */
export const TYPE_META = {
  meeting: { label: 'Réunion', icon: Video, color: 'blue' },
  appointment: { label: 'Rendez-vous', icon: Clock, color: 'green' },
  reminder: { label: 'Rappel', icon: Bell, color: 'purple' },
  task: { label: 'Tâche', icon: Target, color: 'orange' },
};

export const STATUS_META = {
  scheduled: { label: 'Planifié', dot: 'bg-green-400', text: 'text-green-400' },
  completed: { label: 'Terminé', dot: 'bg-blue-400', text: 'text-blue-400' },
  cancelled: { label: 'Annulé', dot: 'bg-red-400', text: 'text-red-400' },
};

export const TYPE_OPTIONS = [
  { value: 'meeting', label: 'Réunion' },
  { value: 'appointment', label: 'Rendez-vous' },
  { value: 'reminder', label: 'Rappel' },
  { value: 'task', label: 'Tâche' },
];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Basse' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'high', label: 'Haute' },
];

export const getTypeMeta = (type) => TYPE_META[type] || TYPE_META.meeting;
export const getStatusMeta = (status) => STATUS_META[status] || STATUS_META.scheduled;

export const getColorClass = (color) => {
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-red-500',
  };
  return colors[color] || colors.blue;
};

export const getPriorityColor = (priority) => {
  const colors = {
    high: 'text-red-400 bg-red-500/20 border-red-500/30',
    medium: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
    low: 'text-green-400 bg-green-500/20 border-green-500/30',
  };
  return colors[priority] || colors.medium;
};

export const getPriorityLabel = (priority) =>
  priority === 'high' ? 'Haute' : priority === 'low' ? 'Basse' : 'Moyenne';

/* ---------- Dates ---------- */

const TIME_FMT = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

/** "10:00 - 11:00" à partir de deux dates ISO. */
export const formatTimeRange = (startDate, endDate) => {
  if (!startDate) return '';
  const start = new Date(startDate);
  if (!endDate) return TIME_FMT.format(start);
  return `${TIME_FMT.format(start)} - ${TIME_FMT.format(new Date(endDate))}`;
};

export const formatLongDate = (date) =>
  new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));

export const formatMonthYear = (date) =>
  new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(new Date(date));

export const isSameDay = (a, b) => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

/** Lundi 00:00 de la semaine contenant `date`. */
export const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = (d.getDay() + 6) % 7; // lundi = 0
  d.setDate(d.getDate() - day);
  return d;
};

export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

export const startOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

export const endOfMonth = (date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
};

/** Valeur pour <input type="datetime-local"> (heure locale, sans le suffixe Z). */
export const toDatetimeLocal = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};
